import {Injectable} from '@angular/core';
import {ELanguageLabel, TLanguageLabel} from '../enums/language';
import {ITranslationsTree, TLanguage} from '../interfaces/language';
import {PLURAL_FORMS} from '../consts/plural-forms';

@Injectable({
  providedIn: 'root',
})
export class I18nService {
  private LANGUAGE: TLanguage = ELanguageLabel.EN;
  private TRANSLATIONS: Partial<Record<ELanguageLabel, Record<TLanguageLabel, string | string[]>>> = {};

  private localizedName = {
    de: 'Deutsch',
    en: 'English',
    es: 'Español',
    fr: 'Français',
    pt: 'Português',
    uk: 'Українська',
  };

  private setTranslations(lang: TLanguage, data: Record<TLanguageLabel, string | string[]>) {
    this.TRANSLATIONS[lang] = this.flattenTranslations(data as ITranslationsTree);
  }

  public getAllowLanguages(): {value: ELanguageLabel; name: string}[] {
    return Object.values(ELanguageLabel)
      .sort()
      .map((value) => ({
        value,
        name: this.localizedName[value] || `--${value}--`,
      }));
  }

  public getDefaultLang() {
    return this.LANGUAGE;
  }

  public async setDefaultLang(lang: TLanguage) {
    if (lang && lang.toUpperCase() in ELanguageLabel) {
      this.LANGUAGE = lang;

      if (lang in this.TRANSLATIONS) {
        return true;
      }

      return await this.appendTranslations(lang);
    }

    return false;
  }

  public async appendTranslations(lang: TLanguage) {
    if (lang in this.TRANSLATIONS) {
      return true;
    }

    // const data = await fetch(`/locales/${lang}.json?ngsw-bypass=1`).then((res) => res.json());
    const data = await fetch(`/locales/${lang}.json`).then((res) => res.json());
    if (!data) {
      return false;
    }

    this.setTranslations(lang, data as Record<TLanguageLabel, string | string[]>);

    return true;
  }

  public translate(label: TLanguageLabel, interpolateParams?: Record<string, string | number>, plural?: number, lang?: ELanguageLabel) {
    const ln = lang ?? this.LANGUAGE;
    if (!this.TRANSLATIONS[ln] || !(label in this.TRANSLATIONS[ln])) {
      if (label) {
        console.warn(`No translation found for label ${label}, lang ${ln}`);
      }
      return label;
    }

    const isPluralLabel = Array.isArray(this.TRANSLATIONS[ln][label]);

    const translationForm = this.getPluralForm(ln, plural || 0);

    const translation = isPluralLabel
      ? (this.TRANSLATIONS[ln][label][translationForm] ?? this.TRANSLATIONS[ln][label][0])
      : (this.TRANSLATIONS[ln][label] as string);

    if (!interpolateParams) {
      return translation;
    }

    return translation.replace(/\{\{[^}]+}}/g, (all: string) => `${interpolateParams[all.replace(/[{}]+/g, '')]}` || all);
  }

  private flattenTranslations(data: ITranslationsTree, parent?: string): Record<TLanguageLabel, string | string[]> {
    return Object.entries(data).reduce(
      (acc, [key, val]) => {
        const newKey: TLanguageLabel = (parent ? `${parent}.${key}` : key) as TLanguageLabel;
        if (typeof val === 'string' || Array.isArray(val)) {
          acc[newKey] = val;
        } else {
          acc = {...acc, ...this.flattenTranslations(val, newKey)};
        }
        return acc;
      },
      {} as Partial<Record<TLanguageLabel, string | string[]>>
    ) as Record<TLanguageLabel, string | string[]>;
  }

  private getPluralForm(lang: TLanguage, number: number): number {
    const n = typeof number === 'number' ? number : 0;
    const formula = PLURAL_FORMS[lang] ?? PLURAL_FORMS[ELanguageLabel.EN];

    return formula(n);
  }
}
